// All 112 meditations from the Vigyan Bhairava Tantra, aligned to the local
// source PDF. Site technique 1 maps to source verse 24, and technique 112 maps
// to source verse 135. The entries below are modern practice notes, not verbatim
// translations.
// Schema: id, sourceVerse, family, title, essence, modern, best, time, level,
// steps, practice, mistake, use, avoid, related, prompt.

const rawTechniques = [
  {
    id: 1, sourceVerse: 24, family: "breath", title: "The Two Origins of Breath", time: 7, level: 1,
    essence: "Attend to the two places where breath begins: after exhale and after inhale.",
    modern: "Let the breath move naturally. Rest attention at the tiny origin point where exhale becomes inhale and inhale becomes exhale.",
    best: ["Breath awareness", "Overthinking", "Settling", "Beginner"],
    steps: ["Sit comfortably and let the breath move on its own.", "Follow one full inhale and one full exhale.", "Notice the quiet origin point before the next movement of breath.", "Keep awareness on the whole breathing cycle, with extra care at each pause.", "Return gently whenever thought carries you away."],
    practice: "Use one unforced breath cycle before beginning work, then expand to a seated practice.",
    mistake: "Holding the breath to manufacture a gap. The source instruction is observation, not control.",
    use: "When the mind is restless and you need a direct, simple anchor.",
    avoid: "Do not practice while driving or during physical exertion.",
    prompt: "What did you notice at the origin point of the breath?",
  },
  {
    id: 2, sourceVerse: 25, family: "breath", title: "The Turning of Breath", time: 7, level: 1,
    essence: "Concentrate where breath turns inward to outward and outward to inward.",
    modern: "Feel the breath turning at both ends of the cycle. The doorway is the turn itself, not a forced pause.",
    best: ["Concentration", "Subtle attention", "Breath awareness"],
    steps: ["Breathe naturally through the nose if comfortable.", "Sense the inhale as it finishes inside.", "Stay present as it turns toward exhale.", "Sense the exhale as it finishes outside.", "Stay present as it turns toward inhale."],
    practice: "During a quiet commute or break, track three complete turns of the breath.",
    mistake: "Looking for a dramatic event. The turn is usually delicate.",
    use: "When you want a precise object of attention.",
    avoid: "Skip if breath observation increases anxiety.",
    prompt: "Was the breath turn obvious, subtle, or hard to locate?",
  },
  {
    id: 3, sourceVerse: 26, family: "breath", title: "The Center Beyond Breath", time: 7, level: 1,
    essence: "At the center where breath neither enters nor leaves, thought falls quiet.",
    modern: "Notice the still point between breath movements. Rest there lightly and let the next breath come by itself.",
    best: ["Stillness", "Anxiety", "Beginner", "Silence"],
    steps: ["Sit with eyes closed or lowered.", "Observe the breath without changing it.", "At each center point, feel the body briefly still.", "Let thought soften without fighting it.", "Continue through many natural cycles."],
    practice: "Use this after a stressful message before replying.",
    mistake: "Trying to stop thought by force.",
    use: "When you need to return from mental speed into stillness.",
    avoid: "Avoid long pauses if you feel light-headed.",
    prompt: "How did the body feel in the center beyond breath?",
  },
  {
    id: 4, sourceVerse: 27, family: "breath", title: "Natural Breath Suspension", time: 10, level: 2,
    essence: "When breath is retained by itself after inhale or exhale, peace is revealed.",
    modern: "After breath awareness deepens, the breath may pause by itself. Notice that peaceful suspension without fear or interference.",
    best: ["Deep practice", "Peace", "Surrender"],
    steps: ["Begin with simple breath awareness.", "Let the breathing grow quiet on its own.", "If the breath pauses naturally, stay relaxed.", "Do not extend or shorten the pause.", "Let the body resume breathing when it wants to."],
    practice: "Reserve this for settled sittings, after several minutes of easier breath practice.",
    mistake: "Turning natural suspension into breath retention training.",
    use: "When the breath has already become quiet and easeful.",
    avoid: "Avoid if you have panic, respiratory issues, pregnancy concerns, or medical advice against breath holds.",
    prompt: "Did peace or fear appear when the breath became quiet?",
  },
  {
    id: 5, sourceVerse: 28, family: "body", title: "Ray Rising Through the Centers", time: 12, level: 3,
    essence: "Meditate on energy as a bright ray rising from the root to the highest center.",
    modern: "Visualize a fine ray of light rising up the central body line, becoming subtler until it dissolves above.",
    best: ["Energy body", "Visualization", "Advanced practice"],
    steps: ["Sit upright and steady.", "Sense the base of the spine or pelvic floor as the root.", "Imagine a bright, fine ray rising through the central channel.", "Let it grow subtler as it rises.", "Rest when it dissolves at the crown."],
    practice: "Use sparingly as a formal seated visualization.",
    mistake: "Forcing energy sensations or chasing special effects.",
    use: "When you are grounded and comfortable with visualization.",
    avoid: "Avoid during dissociation, panic, or destabilizing energy experiences.",
    prompt: "Where did the imagined ray feel clearest?",
  },
  {
    id: 6, sourceVerse: 29, family: "body", title: "Lightning Through the Centers", time: 12, level: 3,
    essence: "Meditate on energy like lightning ascending center by center into great love.",
    modern: "Picture a brief lightning-like current rising through the subtle centers, ending in an expansive feeling of love.",
    best: ["Energy body", "Devotion", "Advanced practice"],
    steps: ["Sit upright with the spine relaxed.", "Bring attention to the root center.", "Imagine a flash moving upward through each center.", "Pause briefly at the crown.", "Let the practice end as a feeling of spacious love."],
    practice: "Use after grounding practices, not as a first meditation of the day.",
    mistake: "Straining the spine or trying to create physical shocks.",
    use: "When the body feels stable and attention is clear.",
    avoid: "Avoid if intense energy practices have previously unsettled you.",
    prompt: "Did the image of lightning open, excite, or unsettle the body?",
  },
  {
    id: 7, sourceVerse: 30, family: "sound", title: "Twelve Letters Into Silence", time: 12, level: 3,
    essence: "Meditate on twelve Sanskrit letters from gross sound to subtle sound, then leave them behind.",
    modern: "Use a chosen sequence of sacred syllables as support, then let the sound become subtle, silent, and finally released.",
    best: ["Mantra", "Sound", "Advanced practice"],
    steps: ["Choose a short syllable sequence from a tradition you respect.", "Recite it audibly for several rounds.", "Recite it softly in the mind.", "Let even the mental sound fade.", "Rest without the support of the syllables."],
    practice: "Practice only with a sequence you can hold respectfully and consistently.",
    mistake: "Treating the syllables as decoration rather than a support to be released.",
    use: "When mantra practice is already familiar.",
    avoid: "Avoid inventing sacred syllables casually if lineage accuracy matters to you.",
    prompt: "What remained when the sound support was released?",
  },
  {
    id: 8, sourceVerse: 31, family: "body", title: "Between the Eyebrows", time: 10, level: 2,
    essence: "Concentrate without thought just above and between the eyebrows.",
    modern: "Rest attention at the brow center until the inner field becomes bright or spacious.",
    best: ["Focus", "Mental quiet", "Inner light"],
    steps: ["Sit with the head level and jaw relaxed.", "Close or lower the eyes.", "Place attention just above and between the eyebrows.", "Do not wrinkle the brow or strain the eyes.", "Rest there without following thoughts."],
    practice: "Use after screen work for a quiet reset.",
    mistake: "Tensing the forehead to feel the point more strongly.",
    use: "When the mind needs a clear single point.",
    avoid: "Avoid during migraine or eye strain.",
    prompt: "Did the brow point feel bright, quiet, pressured, or neutral?",
  },
  {
    id: 9, sourceVerse: 32, family: "space", title: "Peacock-Tail Voids", time: 12, level: 3,
    essence: "Meditate on five colored circles as voids until they dissolve into the supreme void.",
    modern: "Use five colored circles as a visual support, then let the colors dissolve into open space.",
    best: ["Visualization", "Void", "Color meditation"],
    steps: ["Imagine five luminous circles like the eyes on a peacock feather.", "Rest attention on the first circle as empty color.", "Move through the remaining circles in the same way.", "Let all five dissolve together.", "Rest in the open space after the image fades."],
    practice: "Try this as a quiet evening visualization.",
    mistake: "Trying to make the colors perfect.",
    use: "When visual imagination is easy and steady.",
    avoid: "Avoid if visual practices make you spaced out.",
    prompt: "What was present after the colors dissolved?",
  },
  {
    id: 10, sourceVerse: 33, family: "space", title: "Absorption Into One Object", time: 10, level: 1,
    essence: "By gradually focusing on space, a wall, or a great person, one is absorbed into reality.",
    modern: "Choose one simple object of attention and stay with it until the separation between observer and object softens.",
    best: ["Concentration", "Beginner", "Visual anchor"],
    steps: ["Choose a plain wall, open space, or inspiring image.", "Let the gaze be steady and relaxed.", "Place attention fully on the chosen object.", "When thoughts arise, return to seeing.", "Notice whether the boundary between you and the object softens."],
    practice: "Use a plain wall for a no-equipment meditation.",
    mistake: "Switching objects whenever the mind gets bored.",
    use: "When sitting with the breath is not appealing.",
    avoid: "Avoid staring if your eyes become dry or strained.",
    prompt: "What happened to the sense of distance between you and the object?",
  },
  {
    id: 11, sourceVerse: 34, family: "body", title: "Inside the Skull", time: 10, level: 2,
    essence: "Seated with eyes closed, fix attention inside the skull.",
    modern: "Close the eyes and sense the inner space of the head. Let attention settle inside that quiet chamber.",
    best: ["Inner awareness", "Concentration", "Stillness"],
    steps: ["Sit comfortably with eyes closed.", "Sense the space inside the skull.", "Let sounds and thoughts appear within that space.", "Keep returning to the inner head space.", "Rest without trying to visualize anything."],
    practice: "Use when mental noise feels located in the head.",
    mistake: "Creating pressure by concentrating too hard.",
    use: "When you want an inward, seated practice.",
    avoid: "Avoid during headaches if it increases pressure.",
    prompt: "Did the inner skull feel crowded or spacious?",
  },
  {
    id: 12, sourceVerse: 35, family: "body", title: "Inner Space of the Central Channel", time: 12, level: 3,
    essence: "Meditate on the subtle inner space of the central channel in the spine.",
    modern: "Sense a fine hollow thread through the middle of the spine and rest attention in its inner space.",
    best: ["Subtle body", "Spine awareness", "Advanced practice"],
    steps: ["Sit upright without stiffness.", "Sense the central line of the spine.", "Imagine it as a very fine hollow thread.", "Rest attention inside that subtle channel.", "Let the practice become quiet rather than visual."],
    practice: "Pair this with a few minutes of grounding first.",
    mistake: "Rigidly straightening the spine until the body hurts.",
    use: "When you can sit steadily and subtly.",
    avoid: "Avoid if spinal focus triggers pain or anxiety.",
    prompt: "Could you sense the central line without forcing posture?",
  },
  {
    id: 13, sourceVerse: 36, family: "body", title: "Brow Light With Senses Closed", time: 10, level: 3,
    essence: "Concentrate at the brow until light appears, then close the seven head openings.",
    modern: "This is an advanced sensory-withdrawal practice. Use only a gentle version: brow attention plus soft closing of the senses.",
    best: ["Advanced practice", "Inner light", "Sensory withdrawal"],
    steps: ["Begin with attention between the eyebrows.", "If inner light appears, notice it without chasing it.", "Optionally cover the ears softly with the hands.", "Keep the eyes, jaw, and breath relaxed.", "Release the practice if strain appears."],
    practice: "Use the gentle version only; the full form belongs with trained guidance.",
    mistake: "Pressing the face, eyes, nose, or ears aggressively.",
    use: "When you already have a stable sitting practice.",
    avoid: "Avoid with eye pressure, sinus issues, panic, or without teacher guidance for the full form.",
    prompt: "Did closing the senses make awareness quieter or more tense?",
  },
  {
    id: 14, sourceVerse: 37, family: "body", title: "Subtle Light at Head or Heart", time: 8, level: 3,
    essence: "Gentle eye pressure may reveal a subtle point of light at the head or heart.",
    modern: "The source describes a pressure-based light practice; for safety, use a non-pressing version and attend to any natural inner light.",
    best: ["Inner light", "Heart awareness", "Advanced practice"],
    steps: ["Close the eyes softly.", "Do not press on the eyeballs.", "Notice any natural dot, glow, or darkness in the inner field.", "If attention moves to the crown or heart, rest there.", "Let the image fade when it fades."],
    practice: "Use after a calming breath practice.",
    mistake: "Pressing the eyes to produce visual effects.",
    use: "When inner visual phenomena arise naturally.",
    avoid: "Avoid eye pressure, especially with eye conditions or headaches.",
    prompt: "Where did subtle light or darkness draw attention?",
  },
  {
    id: 15, sourceVerse: 38, family: "sound", title: "River Sound and Inner Sound", time: 10, level: 1,
    essence: "Bathe in continuous natural sound, or close the ears and hear the inner unstruck sound.",
    modern: "Listen deeply to a steady sound such as water, wind, fan noise, or the inner hum revealed in quiet.",
    best: ["Sound", "Sleep", "Beginner", "Calming"],
    steps: ["Choose a continuous sound.", "Let the sound fill attention.", "Listen to the sound as vibration, not as a named object.", "If external sound is absent, cup the ears gently and hear the inner hum.", "Rest in listening."],
    practice: "Try it with rain, a shower, or a fan.",
    mistake: "Analyzing the sound instead of bathing in it.",
    use: "When sound is easier than breath.",
    avoid: "Avoid pressing hard on the ears.",
    prompt: "Did listening feel external, internal, or both?",
  },
  {
    id: 16, sourceVerse: 39, family: "sound", title: "AUM Fading Into Void", time: 8, level: 1,
    essence: "Chant AUM slowly and concentrate on the void at the end of the sound.",
    modern: "Let a long AUM fade completely, then rest in the silence after it.",
    best: ["Mantra", "Silence", "Beginner"],
    steps: ["Sit upright and inhale comfortably.", "Chant AUM slowly on the exhale.", "Listen as the sound fades.", "When the sound ends, stay with the silence.", "Repeat gently."],
    practice: "Use three slow chants before silent sitting.",
    mistake: "Focusing on vocal performance instead of the ending silence.",
    use: "When you need a sound doorway into stillness.",
    avoid: "Avoid loud chanting if it strains the throat.",
    prompt: "What was the silence like after the sound ended?",
  },
  {
    id: 17, sourceVerse: 40, family: "sound", title: "The Void Around Any Sound", time: 6, level: 1,
    essence: "Concentrate on the void at the beginning or end of any letter or sound.",
    modern: "Use any simple sound. Notice the silence before it begins and after it ends.",
    best: ["Sound", "Quick reset", "Silence"],
    steps: ["Choose a simple syllable or listen for a natural sound.", "Notice the silence before the sound.", "Let the sound happen.", "Notice the silence after it ends.", "Stay with the silence rather than repeating too quickly."],
    practice: "Use the start and end of a notification sound as a mindfulness bell.",
    mistake: "Missing the silence by rushing to the next repetition.",
    use: "When the environment already has intermittent sounds.",
    avoid: "Avoid using harsh or startling sounds.",
    prompt: "Was the void clearer before the sound or after it?",
  },
  {
    id: 18, sourceVerse: 41, family: "sound", title: "The Fading Edge of Music", time: 10, level: 1,
    essence: "Listen to the end of sustained musical sound until it diminishes into space.",
    modern: "Let a bell, string, bowl, or instrument ring out. Follow the fading edge into silence.",
    best: ["Music", "Listening", "Beginner", "Focus"],
    steps: ["Play one sustained tone or listen to a ringing instrument.", "Give full attention to the sound.", "Follow it as it becomes quieter.", "Stay with the last trace.", "Rest in the space after it disappears."],
    practice: "Use one bell tone before beginning a meditation session.",
    mistake: "Playing continuous music and never meeting the ending.",
    use: "When music naturally draws your attention.",
    avoid: "Avoid high volume or headphones that fatigue the ears.",
    prompt: "How long could you hear the fading edge?",
  },
  {
    id: 19, sourceVerse: 42, family: "sound", title: "Where AUM Ends", time: 8, level: 1,
    essence: "Chant AUM audibly and concentrate where the sound ends into the void.",
    modern: "The doorway is the exact vanishing point of the chant. Follow AUM until only silence remains.",
    best: ["Mantra", "Void", "Sound"],
    steps: ["Inhale naturally.", "Chant AUM on the exhale.", "Listen as the sound reduces.", "Place attention on the point where sound becomes silence.", "Rest there before the next chant."],
    practice: "Use this when the mind needs a clear transition into silence.",
    mistake: "Thinking the chant is the whole practice; the end of sound is the doorway.",
    use: "Before silent meditation or sleep.",
    avoid: "Avoid if chanting agitates you.",
    prompt: "Could you feel a point where sound became silence?",
  },
  {
    id: 20, sourceVerse: 43, family: "space", title: "Body Pervaded by Space", time: 10, level: 2,
    essence: "Concentrate on the body while imagining space pervading all directions.",
    modern: "Feel the body not as a solid block but as space opening in every direction.",
    best: ["Embodiment", "Spaciousness", "Tension release"],
    steps: ["Sit or lie down comfortably.", "Feel the whole body at once.", "Imagine space inside and around the body.", "Let front, back, above, below, left, and right open equally.", "Rest as the body-space field."],
    practice: "Use after a day of feeling physically tight or crowded.",
    mistake: "Escaping the body instead of sensing it as spacious.",
    use: "When tension makes the body feel contracted.",
    avoid: "Avoid if spaciousness causes dissociation.",
    prompt: "Which direction of space felt easiest to sense?",
  },
  {
    id: 21, sourceVerse: 44, family: "space", title: "Above and Below as Void", time: 10, level: 2,
    essence: "Meditate on the space above and the base below as void at the same time.",
    modern: "Hold awareness of the space above the head and beneath the body until thought loosens.",
    best: ["Void", "Posture", "Spaciousness"],
    steps: ["Sit upright.", "Feel the space above the crown.", "Feel the base below the body.", "Hold both in attention together.", "Let the body be held between two open spaces."],
    practice: "Use when posture feels heavy or collapsed.",
    mistake: "Alternating too quickly instead of sensing both at once.",
    use: "When you want a spatial meditation without visualization.",
    avoid: "Avoid if it makes you dizzy or ungrounded.",
    prompt: "Did awareness expand upward, downward, or both?",
  },
  {
    id: 22, sourceVerse: 45, family: "space", title: "Above, Below, and Heart as Void", time: 12, level: 2,
    essence: "Meditate on the above, the base, and the heart as void.",
    modern: "Sense open space above you, below you, and in the heart center at the same time.",
    best: ["Heart space", "Void", "Stillness"],
    steps: ["Sit steady and relaxed.", "Sense space above the head.", "Sense space below the body.", "Sense space in the heart center.", "Let the three spaces become one open field."],
    practice: "Use when emotion is present but you want space around it.",
    mistake: "Trying to feel emptiness as numbness.",
    use: "When you want a heart-centered void practice.",
    avoid: "Avoid during acute grief if it feels like shutdown.",
    prompt: "How did the heart feel when held as space?",
  },
  {
    id: 23, sourceVerse: 46, family: "body", title: "One Body Part as Void", time: 6, level: 1,
    essence: "Consider any part of the body as only void for a short while.",
    modern: "Choose one body part and sense it as spacious rather than solid.",
    best: ["Body awareness", "Beginner", "Tension"],
    steps: ["Choose one area, such as a hand.", "Feel its shape and sensations.", "Imagine the area as open space.", "Let sensations float in that space.", "Rest for a short while."],
    practice: "Try this with a tense hand, jaw, or shoulder.",
    mistake: "Denying sensation instead of letting sensation appear within space.",
    use: "When one area of the body is calling for attention.",
    avoid: "Avoid on injured areas if it disconnects you from needed care.",
    prompt: "Did the chosen body part feel solid, spacious, or both?",
  },
  {
    id: 24, sourceVerse: 47, family: "body", title: "Body Elements as Empty Space", time: 10, level: 2,
    essence: "Consider every constituent of the body to be pervaded by empty space.",
    modern: "Scan the body and sense each region as porous, open, and filled with space.",
    best: ["Body scan", "Spaciousness", "Subtle awareness"],
    steps: ["Sit or lie down.", "Move attention slowly through the body.", "At each region, sense space within the tissues.", "Let the whole body become porous.", "Rest as openness filled with sensation."],
    practice: "Use before sleep when the body feels dense or tense.",
    mistake: "Rushing the scan.",
    use: "When a full-body spaciousness practice feels supportive.",
    avoid: "Avoid if it increases dissociation.",
    prompt: "Which part of the body most easily felt spacious?",
  },
  {
    id: 25, sourceVerse: 48, family: "body", title: "Skin as the Wall of Emptiness", time: 10, level: 2,
    essence: "Consider the skin as a wall around an empty body.",
    modern: "Feel the outline of the skin and imagine the inside of the body as clear open space.",
    best: ["Body boundary", "Void", "Embodiment"],
    steps: ["Sit quietly and feel the surface of the skin.", "Sense the body's outer boundary.", "Imagine everything inside that boundary as open space.", "Let sensations arise inside the openness.", "Rest with both boundary and emptiness."],
    practice: "Use when you feel either too boundaryless or too rigid.",
    mistake: "Making emptiness feel cold or lifeless.",
    use: "When body-boundary awareness is helpful.",
    avoid: "Avoid if body-image practices are triggering.",
    prompt: "What changed when the inside of the body became spacious?",
  },
  {
    id: 26, sourceVerse: 49, family: "body", title: "The Heart Lotus Center", time: 12, level: 3,
    essence: "When senses are absorbed in the heart's inner space, concentrate on its center.",
    modern: "Gather attention into the heart space and rest at the subtle center there.",
    best: ["Heart center", "Subtle body", "Devotion"],
    steps: ["Sit with a calm posture.", "Let the senses turn inward.", "Bring attention to the inner space of the heart.", "Find the quiet center of that space.", "Rest there with undivided attention."],
    practice: "Use after a few minutes of breath awareness.",
    mistake: "Treating the heart as only the physical organ.",
    use: "When the heart feels available and steady.",
    avoid: "Avoid if heart focus intensifies anxiety or grief.",
    prompt: "Did the heart center feel emotional, quiet, or spacious?",
  },
  {
    id: 27, sourceVerse: 50, family: "breath", title: "Where the Inhale Ends Inside", time: 8, level: 2,
    essence: "Absorb the mind at the center of the body where the inhaled breath ends.",
    modern: "Follow the inhale inward and settle attention at the felt point where it completes inside the body.",
    best: ["Breath awareness", "Inner focus", "Concentration"],
    steps: ["Sit quietly and inhale naturally.", "Sense where the inhale seems to end inside.", "Do not decide by anatomy; feel it directly.", "Rest attention at that point.", "Return there after each inhale."],
    practice: "Use when you want a more embodied breath practice.",
    mistake: "Forcing the breath deep to find a dramatic endpoint.",
    use: "When breath and body awareness can work together.",
    avoid: "Avoid if deep breathing causes strain.",
    prompt: "Where did the inhale seem to complete itself?",
  },
  {
    id: 28, sourceVerse: 51, family: "daily", title: "Daylong Gap Between Breaths", time: 5, level: 2,
    essence: "In every place and activity, fix attention between two breaths.",
    modern: "Carry breath-gap awareness into ordinary life, returning again and again throughout the day.",
    best: ["Daily awareness", "Breath", "Habit"],
    steps: ["Begin with a short seated breath-gap practice.", "Choose ordinary transitions as reminders.", "Notice one gap between breaths.", "Continue your activity without losing the thread completely.", "Return whenever you remember."],
    practice: "Use doorways, messages, and task switches as reminders to notice one breath gap.",
    mistake: "Trying to maintain intense concentration all day.",
    use: "When you want meditation woven into daily life.",
    avoid: "Do not practice in a way that distracts from safety-critical tasks.",
    prompt: "Which daily moment best reminded you to return to the breath gap?",
  },
  {
    id: 29, sourceVerse: 52, family: "body", title: "Body Burned to Calm Splendor", time: 12, level: 3,
    essence: "Imagine the body burned by fire from the right foot to the crown.",
    modern: "Use fire as a visualization of release: the body-image burns away and calm clarity remains.",
    best: ["Release", "Visualization", "Transformation"],
    steps: ["Sit steady and grounded.", "Imagine a purifying fire beginning at the right foot.", "Let it move slowly upward through the body-image.", "Do not imagine harm; imagine release of solidity.", "Rest in the calm clarity after the image burns out."],
    practice: "Use when you want to release a rigid self-image.",
    mistake: "Making the visualization violent or frightening.",
    use: "When symbolic fire feels clarifying.",
    avoid: "Avoid if fire imagery is traumatic or destabilizing.",
    prompt: "What remained after the body-image burned away?",
  },
  {
    id: 30, sourceVerse: 53, family: "space", title: "The World Burned to Stillness", time: 12, level: 3,
    essence: "Meditate that the entire world is burned by fire.",
    modern: "Imagine all appearances dissolving into a purifying fire, then rest in the stillness after the world-image is gone.",
    best: ["Letting go", "World-image", "Advanced visualization"],
    steps: ["Sit in a grounded posture.", "Imagine the world of appearances as an image before awareness.", "Let a purifying fire dissolve that image.", "Do not dwell on destruction; attend to release.", "Rest in the clear space after the image fades."],
    practice: "Use rarely, as a formal contemplative visualization.",
    mistake: "Indulging catastrophic imagery instead of releasing appearances.",
    use: "When you are steady enough for strong symbolic practice.",
    avoid: "Avoid during grief, fear spirals, or trauma activation.",
    prompt: "Was the fire image frightening, freeing, or neutral?",
  },
  {
    id: 31, sourceVerse: 54, family: "space", title: "Elements Dissolving to Subtlety", time: 12, level: 2,
    essence: "Meditate that the body's or world's elements become subtler until they disappear.",
    modern: "Let the felt elements of solidity, fluidity, heat, movement, and space refine into subtler and subtler presence.",
    best: ["Element meditation", "Dissolution", "Subtle awareness"],
    steps: ["Sit and feel the body or the surrounding world.", "Notice solidity, fluidity, warmth, movement, and space.", "Imagine each becoming subtler.", "Let the whole field refine until it is almost transparent.", "Rest in what remains."],
    practice: "Use this after a simple body scan.",
    mistake: "Turning the elements into an intellectual list.",
    use: "When you want a structured dissolution practice.",
    avoid: "Avoid if subtle practices make you ungrounded.",
    prompt: "Which element was easiest to sense or release?",
  },
  {
    id: 32, sourceVerse: 55, family: "breath", title: "Slow Breath With Sound", time: 8, level: 1,
    essence: "Inhale and exhale slowly with sound, meditating on where the breath ends.",
    modern: "Let the breath become slow and audible. Use the sound to stay present at each ending point.",
    best: ["Stress", "Breath", "Sound", "Beginner"],
    steps: ["Sit comfortably.", "Breathe slowly enough that the breath makes a soft sound.", "Listen through the inhale to its ending.", "Listen through the exhale to its ending.", "Rest attention at both endings."],
    practice: "Use a quiet audible breath before a difficult conversation.",
    mistake: "Making the breath loud or strained.",
    use: "When soft breath sound helps you stay attentive.",
    avoid: "Avoid if slow breathing feels air-hungry.",
    prompt: "Did sound make it easier to stay with the breath endings?",
  },
  {
    id: 33, sourceVerse: 56, family: "space", title: "Universe Dissolving Gross to Subtle", time: 12, level: 2,
    essence: "Consider the universe dissolving from gross to subtle, and subtle to supreme, until mind dissolves.",
    modern: "Let the world in awareness refine from solid appearance into subtle presence, then into silence.",
    best: ["Dissolution", "Contemplation", "Stillness"],
    steps: ["Sit and include the whole field of experience.", "Notice the gross forms: body, room, sounds.", "Imagine them becoming subtle.", "Let subtle presence dissolve into silence.", "Rest without needing to rebuild the world-image."],
    practice: "Use at the end of a longer sit.",
    mistake: "Making it a cosmology lesson rather than direct contemplation.",
    use: "When the mind is ready to let appearances soften.",
    avoid: "Avoid if reality-testing feels unstable.",
    prompt: "What was hardest to let become subtle?",
  },
  {
    id: 34, sourceVerse: 57, family: "space", title: "The Universe as Part of Shiva", time: 10, level: 2,
    essence: "Meditate that the whole universe to its limits is part of Shiva.",
    modern: "Let every direction and object be included in one sacred field of awareness.",
    best: ["Nondual contemplation", "Awe", "Belonging"],
    steps: ["Sit or stand with a wide sense of space.", "Let attention include the whole room.", "Extend inclusion outward as far as imagination allows.", "Sense everything as belonging to one sacred field.", "Rest without excluding anything."],
    practice: "Use under the sky or in a quiet room.",
    mistake: "Trying to believe something abstract instead of sensing inclusion.",
    use: "When separation feels strong.",
    avoid: "Use secular wording if devotional language does not fit your practice.",
    prompt: "What changed when nothing was outside the field?",
  },
  {
    id: 35, sourceVerse: 58, family: "space", title: "The Universe as Void", time: 10, level: 2,
    essence: "Consider the entire universe to be void so the mind dissolves into the void.",
    modern: "Contemplate all appearances as open, empty, and not as solid as they seem.",
    best: ["Void", "Attachment", "Deep quiet"],
    steps: ["Sit with eyes open or closed.", "Let the whole field of experience be present.", "Sense each appearance as empty space taking shape.", "Do not push anything away.", "Rest in the openness of the whole field."],
    practice: "Use when experience feels too solid or heavy.",
    mistake: "Confusing void with nihilism or numbness.",
    use: "When you can stay warm and grounded while contemplating emptiness.",
    avoid: "Avoid during depression if emptiness feels bleak.",
    prompt: "Did void feel cold, spacious, peaceful, or alive?",
  },
  {
    id: 36, sourceVerse: 59, family: "space", title: "The Bowl Without Partitions", time: 8, level: 1,
    essence: "Look at a vessel without seeing its partitions, and become absorbed into space.",
    modern: "Look into a bowl, cup, or room-like container and attend to the space it holds rather than its walls.",
    best: ["Visual anchor", "Beginner", "Space"],
    steps: ["Place a bowl or cup in front of you.", "Look softly into its open space.", "Let the rim and walls become secondary.", "Attend to the space inside and around it.", "Rest as attention becomes spacious."],
    practice: "Use a teacup or bowl after meals as a short practice.",
    mistake: "Studying the object's design instead of the space.",
    use: "When you want a concrete space meditation.",
    avoid: "Avoid straining the eyes.",
    prompt: "Could you see the space more than the container?",
  },
  {
    id: 37, sourceVerse: 60, family: "space", title: "Vast Open Place", time: 10, level: 1,
    essence: "Cast sight on a vast open place without trees, mountains, or walls.",
    modern: "Look into open space such as sky, field, horizon, or an uncluttered expanse until the mind relaxes its boundaries.",
    best: ["Sky", "Overthinking", "Beginner", "Awe"],
    steps: ["Find a safe open view.", "Let the gaze rest without scanning.", "Receive the openness in front of you.", "When thoughts arise, return to the vastness.", "Let the mind become simple and wide."],
    practice: "Use a window view of sky when you cannot go outside.",
    mistake: "Searching for something interesting in the open space.",
    use: "When mental clutter needs a wide field.",
    avoid: "Do not practice while walking near traffic or edges.",
    prompt: "What did the mind do when it met a vast open field?",
  },
  {
    id: 38, sourceVerse: 61, family: "space", title: "Between Two Thoughts", time: 8, level: 2,
    essence: "When two thoughts are known, leave both aside and reside in the center between them.",
    modern: "Notice one thought, then another. Instead of following either, rest in the gap or center between them.",
    best: ["Overthinking", "Thoughts", "Stillness"],
    steps: ["Sit quietly and let thoughts appear.", "Recognize a thought without entering it.", "Recognize the next thought without entering it.", "Feel the center between them.", "Rest there whenever the gap appears."],
    practice: "Use after a planning spiral to taste the space between thoughts.",
    mistake: "Trying to stop thoughts before you can notice them.",
    use: "When you can observe thought without needing to solve it.",
    avoid: "Avoid if obsessive thoughts require clinical support.",
    prompt: "Was there a center between thoughts, even briefly?",
  },
  {
    id: 39, sourceVerse: 62, family: "space", title: "After One Thought, Before the Next", time: 8, level: 2,
    essence: "When one thought has left and another has not yet come, true nature blossoms in the middle.",
    modern: "Catch the exact after-thought moment. Rest before the mind moves toward the next thought.",
    best: ["Thought gap", "Meditation depth", "Mental quiet"],
    steps: ["Let the mind think naturally.", "Notice the moment a thought finishes.", "Do not reach for the next thought.", "Rest in the middle state.", "Repeat patiently."],
    practice: "Use this when journaling pauses between sentences.",
    mistake: "Waiting tensely for the next thought.",
    use: "When the mind is slowing down enough to notice endings.",
    avoid: "Avoid making it a mental performance test.",
    prompt: "What was present after a thought ended?",
  },
  {
    id: 40, sourceVerse: 63, family: "space", title: "Everything as Consciousness", time: 10, level: 2,
    essence: "With a thought-free mind, consider the body or universe to be consciousness.",
    modern: "Let everything you sense be known as appearances within awareness, not separate from awareness.",
    best: ["Awareness", "Nondual contemplation", "Depth"],
    steps: ["Sit and settle the breath.", "Feel the body as something known in awareness.", "Include sounds, space, and thoughts.", "Consider the whole field as consciousness appearing.", "Rest without trying to locate awareness."],
    practice: "Use after a few minutes of body or sound practice.",
    mistake: "Debating the idea instead of resting in direct knowing.",
    use: "When attention is clear and reflective.",
    avoid: "Avoid if nondual inquiry feels destabilizing.",
    prompt: "Could you find anything outside awareness?",
  },
  {
    id: 41, sourceVerse: 64, family: "breath", title: "The Meeting Points of Breath", time: 8, level: 1,
    essence: "Concentrate on either meeting point of breath, internal or external.",
    modern: "Choose one breath junction: the inner meeting after inhale or the outer meeting after exhale. Stay there.",
    best: ["Breath", "Focus", "Beginner"],
    steps: ["Sit comfortably.", "Choose the inner or outer breath meeting point.", "Follow the breath to that meeting point.", "Rest there lightly.", "Return to the same point each cycle."],
    practice: "Pick only one meeting point for a week to build steadiness.",
    mistake: "Switching between points so quickly that attention never settles.",
    use: "When one precise breath point feels enough.",
    avoid: "Avoid if breath focus feels claustrophobic.",
    prompt: "Which meeting point felt more natural: inner or outer?",
  },
  {
    id: 42, sourceVerse: 65, family: "emotion", title: "Body or World Full of Bliss", time: 10, level: 2,
    essence: "Consider the body or universe to be full of one's own bliss.",
    modern: "Let a small felt sense of ease spread through the body or world-field as nectar-like bliss.",
    best: ["Joy", "Embodiment", "Devotional mood"],
    steps: ["Begin with any small pleasant sensation.", "Let it be felt without grasping.", "Imagine the body filled with that ease.", "If natural, extend it to the surrounding world.", "Rest in the fullness without forcing it."],
    practice: "Use after gratitude, prayer, or a naturally peaceful moment.",
    mistake: "Manufacturing happiness over real pain.",
    use: "When a genuine seed of ease is present.",
    avoid: "Avoid using bliss practice to bypass difficult emotion.",
    prompt: "Was there a real seed of bliss, however small?",
  },
  {
    id: 43, sourceVerse: 66, family: "emotion", title: "Joy in Caress", time: 5, level: 2,
    essence: "Great joy can arise while being caressed; through that joy, true nature appears.",
    modern: "With safe, consenting touch, attend to the joy of touch itself rather than the story around it.",
    best: ["Touch", "Joy", "Relationship", "Embodiment"],
    steps: ["Use only safe, welcome touch.", "Let attention rest in the felt sensation.", "Notice any instant joy or warmth.", "Stay with the joy rather than the person-story.", "Let the joy reveal simple presence."],
    practice: "This can be as simple as feeling a hand held by someone trusted.",
    mistake: "Using touch without consent or turning the practice into demand.",
    use: "With trusted, consenting touch or gentle self-touch.",
    avoid: "Avoid if touch feels unsafe, coercive, or triggering.",
    prompt: "Could joy be felt as sensation rather than a story?",
  },
  {
    id: 44, sourceVerse: 67, family: "body", title: "Closing the Senses", time: 8, level: 3,
    essence: "By closing the senses, life energy rises through the center and joy spreads.",
    modern: "Use a gentle sensory-withdrawal version: close the eyes, reduce sound, and feel energy gather inward.",
    best: ["Sensory withdrawal", "Energy", "Advanced practice"],
    steps: ["Sit in a quiet place.", "Close the eyes and let the mouth relax.", "Optionally cover the ears softly for a few breaths.", "Feel attention gather along the center of the body.", "Release the senses slowly and gently."],
    practice: "Use briefly after calming breath practice.",
    mistake: "Blocking the senses harshly or holding the body rigid.",
    use: "When inward attention feels safe and steady.",
    avoid: "Avoid if sensory restriction causes panic.",
    prompt: "Did turning inward create joy, pressure, or quiet?",
  },
  {
    id: 45, sourceVerse: 68, family: "love", title: "Joy Through Lovemaking", time: 10, level: 3,
    essence: "Attend to the joy between the beginning and end of lovemaking.",
    modern: "In a safe adult consensual context, let attention stay with the joy and energy of intimacy rather than fantasy or performance.",
    best: ["Intimacy", "Love", "Embodiment", "Advanced practice"],
    steps: ["Practice only with explicit consent and emotional safety.", "Stay present with sensation and affection.", "Notice joy as energy in the body.", "Do not rush toward a goal.", "Let the joy be held in awareness."],
    practice: "Treat this as contemplative intimacy, not a technique to impose on a partner.",
    mistake: "Using a partner as an object for practice.",
    use: "Only in mature, mutually consenting intimacy.",
    avoid: "Avoid where consent, trust, privacy, or emotional readiness is absent.",
    prompt: "Could intimacy remain awareness rather than performance?",
  },
  {
    id: 46, sourceVerse: 69, family: "love", title: "Climactic Joy as a Doorway", time: 5, level: 3,
    essence: "At sexual climax, great joy speaks of one's own divine nature.",
    modern: "The source points to intense pleasure as a doorway: notice the joy and the brief dissolving of self-preoccupation.",
    best: ["Intimacy", "Joy", "Advanced practice"],
    steps: ["Approach only in a safe adult context.", "When intense joy arises, stay aware.", "Notice the vanishing of ordinary self-concern.", "Do not cling to the state.", "Afterward, rest quietly and observe."],
    practice: "Use reflection after intimacy rather than trying to control the moment.",
    mistake: "Chasing climax as spirituality.",
    use: "For mature practitioners reflecting on embodied joy.",
    avoid: "Avoid if sexual practice is compulsive, unsafe, or emotionally harmful.",
    prompt: "What was revealed when self-concern briefly relaxed?",
  },
  {
    id: 47, sourceVerse: 70, family: "love", title: "Remembered Joy of Union", time: 6, level: 3,
    essence: "Even in absence, memory of intense union can bring a flood of joy.",
    modern: "Remember a moment of profound embodied joy and attend to the joy itself as it returns in memory.",
    best: ["Memory", "Joy", "Love", "Advanced practice"],
    steps: ["Sit quietly and choose a memory that feels safe.", "Recall the felt joy without replaying fantasy.", "Find the present sensation of that remembered joy.", "Stay with the sensation itself.", "Let the memory dissolve into present awareness."],
    practice: "Use only memories that bring warmth, not longing or compulsion.",
    mistake: "Getting lost in fantasy instead of meditating on present joy.",
    use: "When memory naturally evokes spacious joy.",
    avoid: "Avoid memories linked to grief, obsession, or harm.",
    prompt: "Could remembered joy become present-moment awareness?",
  },
  {
    id: 48, sourceVerse: 71, family: "love", title: "Joy of Reunion", time: 5, level: 1,
    essence: "When joy arises on seeing a loved one after long separation, meditate on that joy.",
    modern: "When genuine reunion joy appears, pause and feel the joy itself before rushing into words.",
    best: ["Love", "Gratitude", "Daily life", "Joy"],
    steps: ["Notice joy when meeting someone dear.", "Pause inwardly for a breath.", "Feel where the joy lives in the body.", "Let the mind absorb into joy.", "Then speak or act from that fullness."],
    practice: "Use when seeing a friend, relative, or beloved after time apart.",
    mistake: "Missing the joy because you move instantly into conversation or phone photos.",
    use: "Whenever reunion naturally opens the heart.",
    avoid: "Do not force joy if a relationship is complicated.",
    prompt: "Where did reunion joy appear in the body?",
  },
  {
    id: 49, sourceVerse: 72, family: "daily", title: "Joy in Eating and Drinking", time: 10, level: 1,
    essence: "From the pleasure of eating and drinking, joy blossoms; become filled with that joy.",
    modern: "Eat or drink slowly enough to notice the joy that taste brings, then stay with the joy rather than consuming automatically.",
    best: ["Eating", "Daily life", "Gratitude", "Beginner"],
    steps: ["Choose a simple food or drink.", "Take one slow bite or sip.", "Notice pleasure as sensation.", "Let the joy blossom without rushing for more.", "Continue with awareness and moderation."],
    practice: "Use the first sip of tea or first bite of a meal.",
    mistake: "Confusing mindful enjoyment with indulgence.",
    use: "When eating can be quiet and attentive.",
    avoid: "Avoid if food practices are triggering; seek supportive care around disordered eating.",
    prompt: "Could taste become joy without rushing?",
  },
  {
    id: 50, sourceVerse: 73, family: "sound", title: "Joy in Singing and Senses", time: 10, level: 1,
    essence: "When singing or sense pleasure brings great joy, become one with that joy.",
    modern: "Let music, singing, fragrance, or beauty open joy. Meditate on the joy rather than grasping the object.",
    best: ["Music", "Joy", "Beauty", "Beginner"],
    steps: ["Choose a wholesome sensory pleasure such as song or fragrance.", "Let the pleasure arise naturally.", "Find the joy in the body.", "Become intimate with the joy itself.", "Let the object recede while joy remains."],
    practice: "Use one song as a meditation, not background noise.",
    mistake: "Chasing more stimulation instead of entering the joy already present.",
    use: "When beauty or song naturally opens you.",
    avoid: "Avoid overstimulating or addictive sensory loops.",
    prompt: "Could joy remain when the song or pleasure ended?",
  },
  {
    id: 51, sourceVerse: 74, family: "daily", title: "Where the Mind Finds Satisfaction", time: 6, level: 1,
    essence: "Wherever the mind finds satisfaction, focus attention there.",
    modern: "When the mind becomes quietly satisfied, stop and attend to that satisfaction as the doorway.",
    best: ["Contentment", "Daily life", "Joy", "Beginner"],
    steps: ["Notice a moment of simple satisfaction.", "Pause instead of moving on.", "Feel the satisfaction in the body and mind.", "Rest attention there.", "Let it reveal quiet bliss."],
    practice: "Use after finishing a small task, drinking water, or seeing something lovely.",
    mistake: "Looking for extraordinary happiness and missing ordinary contentment.",
    use: "Any time the mind naturally says, enough.",
    avoid: "Do not use satisfaction to ignore needed action or boundaries.",
    prompt: "What simple satisfaction did you almost overlook?",
  },
  {
    id: 52, sourceVerse: 75, family: "sleep", title: "The Threshold of Sleep", time: 8, level: 2,
    essence: "Concentrate where sleep has not fully appeared but the external world has disappeared.",
    modern: "As you are falling asleep, notice the in-between state where the world fades but sleep has not taken over.",
    best: ["Sleep", "Dream edge", "Subtle awareness"],
    steps: ["Lie down comfortably.", "Let the body move toward sleep.", "Notice when the room and day begin to fade.", "Stay gently aware without waking yourself up.", "Rest in the threshold."],
    practice: "Use at bedtime as a soft awareness practice.",
    mistake: "Trying so hard to stay aware that sleep is blocked.",
    use: "When falling asleep is easeful enough to observe.",
    avoid: "Avoid on nights when insomnia is active.",
    prompt: "Could you sense the edge before sleep arrived?",
  },
  {
    id: 53, sourceVerse: 76, family: "space", title: "Colors at the Edge of Light", time: 8, level: 1,
    essence: "Fix sight where light from sun, lamp, or another source forms different colors.",
    modern: "Look softly near reflected or diffused light and attend to subtle color, without staring into the source.",
    best: ["Visual meditation", "Color", "Beginner"],
    steps: ["Use safe reflected or indirect light.", "Look near the colored edge or glow, not into the light source.", "Let the gaze soften.", "Notice color as pure appearance.", "Rest in seeing."],
    practice: "Use sunset color on a wall or lamp glow through fabric.",
    mistake: "Staring directly at bright light.",
    use: "When gentle color naturally draws attention.",
    avoid: "Never stare at the sun or intense light; avoid with eye sensitivity.",
    prompt: "What did color feel like when you stopped naming it?",
  },
  {
    id: 54, sourceVerse: 77, family: "body", title: "The Five Mudras", time: 10, level: 3,
    essence: "Specific yogic mudras reveal the supreme reality.",
    modern: "The source names advanced mudras. Treat this as a teacher-guided practice category rather than a casual instruction.",
    best: ["Mudra", "Advanced practice", "Teacher guided"],
    steps: ["Acknowledge this as an advanced traditional practice.", "Do not improvise unfamiliar mudras from names alone.", "If trained, practice only the mudra as taught.", "Keep attention on awareness, not achievement.", "Close by grounding the body."],
    practice: "Use this entry as a reminder to seek qualified instruction for advanced mudra work.",
    mistake: "Attempting techniques from names without transmission or context.",
    use: "When working with a competent teacher.",
    avoid: "Avoid self-teaching advanced mudras from brief descriptions.",
    prompt: "What advanced practices should remain teacher-guided for you?",
  },
  {
    id: 55, sourceVerse: 78, family: "body", title: "One-Buttock Unsupported Seat", time: 6, level: 2,
    essence: "Sit with one buttock on a soft seat and no support for hands or feet.",
    modern: "Use a mildly unstable seated posture to sharpen balance and present-moment awareness.",
    best: ["Posture", "Balance", "Embodiment"],
    steps: ["Use a soft, safe seat close to the ground.", "Shift weight gently onto one sitting bone.", "Keep hands and feet relaxed without leaning on them.", "Feel balance become vivid.", "Stop before strain appears."],
    practice: "Try briefly, then return to a normal seated posture for integration.",
    mistake: "Turning the posture into endurance or pain practice.",
    use: "When the body is healthy and balance practice feels safe.",
    avoid: "Avoid with back, hip, pelvic, or balance issues.",
    prompt: "How did mild imbalance affect attention?",
  },
  {
    id: 56, sourceVerse: 79, family: "body", title: "Arms Arched Overhead", time: 5, level: 2,
    essence: "Curve the arms overhead like an arch and absorb the mind in the armpit space.",
    modern: "Raise the arms gently overhead and rest attention in the open space beneath the arms.",
    best: ["Posture", "Body space", "Energy"],
    steps: ["Sit comfortably.", "Lift the arms overhead in a soft arch.", "Keep shoulders relaxed.", "Sense the space in the armpits and side body.", "Lower the arms before fatigue becomes strain."],
    practice: "Use for a short embodied reset after sitting at a desk.",
    mistake: "Holding the arms too long or tightening the shoulders.",
    use: "When the upper body needs opening and attention.",
    avoid: "Avoid with shoulder, neck, or circulation issues.",
    prompt: "What did the space under the arms reveal?",
  },
  {
    id: 57, sourceVerse: 80, family: "space", title: "Unblinking Object Gaze", time: 5, level: 2,
    essence: "Look firmly without blinking at the gross form of any object until the mind loses support.",
    modern: "Gaze steadily at a simple object, gently reducing blink and thought without straining the eyes.",
    best: ["Gazing", "Concentration", "Visual anchor"],
    steps: ["Choose a simple object at eye level.", "Let the gaze become steady.", "Blink naturally if the eyes need it.", "Stay with the object's form without naming it.", "Notice the mind losing its usual commentary."],
    practice: "Use a candle substitute such as a stone or cup if flame is distracting.",
    mistake: "Forcing unblinking until the eyes hurt.",
    use: "When visual attention is strong.",
    avoid: "Avoid with dry eyes, eye strain, or migraine.",
    prompt: "Did the object become more vivid or less solid?",
  },
  {
    id: 58, sourceVerse: 81, family: "sound", title: "Tongue, Palate, and Ha", time: 5, level: 3,
    essence: "With mouth open and tongue upward, attend to the middle of the tongue and the sound Ha.",
    modern: "The source gives an advanced mouth-and-sound practice. Use a gentle version: sense the tongue and softly exhale Ha.",
    best: ["Sound", "Mouth awareness", "Advanced practice"],
    steps: ["Sit upright with the jaw relaxed.", "Open the mouth only comfortably.", "Lift the tongue gently toward the palate without strain.", "Exhale a soft Ha.", "Feel the tongue and the end of the sound."],
    practice: "Use briefly, then close the mouth and rest in silence.",
    mistake: "Overstretching the tongue or jaw.",
    use: "When subtle sound-body practice feels appropriate.",
    avoid: "Avoid with jaw pain, tongue strain, or throat irritation.",
    prompt: "Where did the Ha sound end in the body?",
  },
  {
    id: 59, sourceVerse: 82, family: "body", title: "Body Without Support", time: 8, level: 2,
    essence: "Seated on a bed or couch, imagine the body to be without support.",
    modern: "While safely supported, imagine the felt body floating without any support, and observe what happens to the mind.",
    best: ["Support", "Surrender", "Body awareness"],
    steps: ["Sit or lie safely on a bed or couch.", "Feel the contact points clearly.", "Now imagine the body has no support.", "Notice any release, fear, or spaciousness.", "Return to feeling the actual support beneath you."],
    practice: "Use before sleep or after a demanding day.",
    mistake: "Ignoring actual physical safety.",
    use: "When surrender and support are live themes.",
    avoid: "Avoid if floating imagery causes anxiety or dissociation.",
    prompt: "What arose when the body imagined no support?",
  },
  {
    id: 60, sourceVerse: 83, family: "daily", title: "Rhythmic Movement", time: 8, level: 1,
    essence: "Experience the rhythmic movement of the body in a moving vehicle or by slow swinging.",
    modern: "Let gentle rhythmic motion become meditation, either as a passenger or with slow safe swaying.",
    best: ["Restlessness", "Movement", "Commute", "Beginner"],
    steps: ["Choose a safe context, such as being a passenger or sitting still.", "Feel the body's natural rhythm or sway.", "Let attention ride the movement.", "Do not add analysis.", "Let motion calm the mind."],
    practice: "As a passenger, feel the motion of the vehicle without looking at the phone.",
    mistake: "Practicing while responsible for driving or safety.",
    use: "When gentle movement is more accessible than stillness.",
    avoid: "Do not do this while driving; avoid if motion sickness is present.",
    prompt: "Did movement make awareness calmer or more awake?",
  },
  {
    id: 61, sourceVerse: 84, family: "space", title: "Clear Sky Gazing", time: 10, level: 1,
    essence: "Look continuously at a clear sky without moving oneself.",
    modern: "Rest the gaze in the clear sky and let the mind take on the sky's openness.",
    best: ["Sky", "Awe", "Beginner", "Mental space"],
    steps: ["Find a safe place to sit or stand.", "Look at clear sky away from the sun.", "Let the body be still.", "Soften the gaze into the open blue or night sky.", "Rest as the mind widens."],
    practice: "Use a safe sky view from a window or balcony.",
    mistake: "Looking near the sun or scanning constantly.",
    use: "When you need spaciousness and simplicity.",
    avoid: "Never stare at the sun; avoid unsafe edges or roads.",
    prompt: "What did the sky do to the shape of your mind?",
  },
  {
    id: 62, sourceVerse: 85, family: "space", title: "Sky Absorbed in the Head", time: 10, level: 2,
    essence: "Meditate that the entire sky is absorbed in one's head.",
    modern: "Imagine the vast sky entering and filling the inner space of the head.",
    best: ["Sky", "Inner space", "Visualization"],
    steps: ["First look at or remember the sky.", "Close the eyes if helpful.", "Imagine the whole sky absorbed into the head.", "Let the inner head become vast and clear.", "Rest in that brightness."],
    practice: "Use after sky gazing for a more inward practice.",
    mistake: "Tensing the head to contain the sky.",
    use: "When sky imagery feels natural.",
    avoid: "Avoid during headaches or if head-space practices create pressure.",
    prompt: "Could the head feel as open as sky?",
  },
  {
    id: 63, sourceVerse: 86, family: "sleep", title: "Three States as One Light", time: 12, level: 3,
    essence: "Know waking, dreaming, and deep sleep as forms of the same reality.",
    modern: "Contemplate waking, dream, and deep sleep as changing states known by one awareness.",
    best: ["Sleep", "Dream", "Nondual contemplation"],
    steps: ["Recall the waking state now.", "Recall a dream as a different mode of knowing.", "Recall deep sleep as darkness or absence.", "Contemplate all three as known states.", "Rest as the awareness to which states appear."],
    practice: "Use in morning reflection after remembering a dream.",
    mistake: "Trying to solve consciousness philosophically.",
    use: "When reflective inquiry is steady.",
    avoid: "Avoid if sleep reflection feeds insomnia.",
    prompt: "What is continuous through waking, dream, and sleep?",
  },
  {
    id: 64, sourceVerse: 87, family: "space", title: "Dark Night Contemplation", time: 12, level: 2,
    essence: "On a completely dark lunar night, concentrate for a long time on darkness.",
    modern: "Sit safely with natural darkness and let attention merge with the dark field.",
    best: ["Darkness", "Night practice", "Void"],
    steps: ["Choose a safe dark place.", "Let the eyes adjust.", "Look into the darkness without searching.", "Feel darkness as a presence, not an absence.", "Rest until the mind quiets."],
    practice: "Use a dark room if outdoor darkness is not safe.",
    mistake: "Creating fear stories about the dark.",
    use: "When darkness feels contemplative and safe.",
    avoid: "Avoid if darkness triggers fear, trauma, or unsafe surroundings.",
    prompt: "Did darkness feel empty, alive, or intimate?",
  },
  {
    id: 65, sourceVerse: 88, family: "space", title: "Inner Darkness With Eyes Closed", time: 10, level: 1,
    essence: "Close the eyes, concentrate on darkness, then see darkness spreading everywhere.",
    modern: "Attend to the darkness behind closed eyes, then open the eyes softly and sense darkness as a field within seeing.",
    best: ["Darkness", "Sleep", "Beginner", "Stillness"],
    steps: ["Close the eyes.", "Rest attention in the darkness before you.", "Let the darkness become steady.", "Open the eyes softly.", "Sense the same depth behind appearances."],
    practice: "Use before sleep instead of looking at a screen.",
    mistake: "Trying to make the darkness visually special.",
    use: "When inner darkness is calming.",
    avoid: "Avoid if closed-eye darkness feels distressing.",
    prompt: "Was the darkness different with eyes closed and open?",
  },
  {
    id: 66, sourceVerse: 89, family: "body", title: "Restraining a Sense Door", time: 5, level: 2,
    essence: "When a sense organ is externally obstructed or restrained, enter the void beyond duality.",
    modern: "Gently reduce one sense input, such as closing the eyes or covering the ears, and notice the space that opens.",
    best: ["Senses", "Void", "Reset"],
    steps: ["Choose one sense door to soften.", "Close the eyes or lightly cover the ears.", "Notice the absence or reduction of input.", "Rest in the space revealed by that absence.", "Release gently."],
    practice: "Close the eyes for one minute during a safe pause.",
    mistake: "Forcing sensory deprivation.",
    use: "When the senses are overloaded.",
    avoid: "Avoid if sensory restriction causes panic or disorientation.",
    prompt: "What void appeared when one sense door quieted?",
  },
  {
    id: 67, sourceVerse: 90, family: "sound", title: "The Vowel A", time: 6, level: 1,
    essence: "Continuously recite the vowel A without M or H.",
    modern: "Chant a simple open A sound and let its vibration carry attention.",
    best: ["Sound", "Voice", "Beginner"],
    steps: ["Sit upright.", "Inhale gently.", "Chant a long open A sound.", "Feel the vibration in the mouth, chest, and head.", "Repeat without strain."],
    practice: "Use a few soft A chants before silent meditation.",
    mistake: "Adding other syllables when the instruction is simple.",
    use: "When a single vowel feels clean and direct.",
    avoid: "Avoid with throat strain or in contexts where sound is disruptive.",
    prompt: "Where did the A sound vibrate most clearly?",
  },
  {
    id: 68, sourceVerse: 91, family: "sound", title: "The End of Ha", time: 6, level: 1,
    essence: "Make the sound H or Ha and concentrate on the end of the sound.",
    modern: "Exhale a soft Ha and follow its ending into unsupported silence.",
    best: ["Sound", "Exhale", "Letting go"],
    steps: ["Inhale comfortably.", "Exhale with a soft Ha.", "Listen and feel as the sound fades.", "Place attention on the end of the sound.", "Rest in the silence after it."],
    practice: "Use one soft Ha after noticing tension.",
    mistake: "Making the sound theatrical or forceful.",
    use: "When exhaling sound helps release effort.",
    avoid: "Avoid if it irritates the throat.",
    prompt: "What happened at the end of Ha?",
  },
  {
    id: 69, sourceVerse: 92, family: "space", title: "Self as Unlimited Sky", time: 10, level: 2,
    essence: "Meditate on one's own self as space, unlimited in all directions.",
    modern: "Instead of locating yourself inside the body, sense yourself as sky-like awareness extending everywhere.",
    best: ["Self-inquiry", "Sky", "Spaciousness"],
    steps: ["Sit comfortably.", "Feel the usual sense of being here.", "Let that sense expand like sky in every direction.", "Do not search for an edge.", "Rest as unsupported awareness."],
    practice: "Use after sky gazing or spacious body practice.",
    mistake: "Imagining escape from the body rather than widening identity.",
    use: "When inquiry feels open and grounded.",
    avoid: "Avoid if boundary dissolution feels unsafe.",
    prompt: "Did the sense of self have an edge?",
  },
  {
    id: 70, sourceVerse: 93, family: "body", title: "Sharp Sensation as Awareness", time: 3, level: 3,
    essence: "The source uses a sharp point and joins awareness to the exact place of sensation.",
    modern: "Do not pierce yourself. Use an existing harmless sensation or a light fingertip press and join awareness to that exact point.",
    best: ["Pain awareness", "Precision", "Advanced adaptation"],
    steps: ["Choose a harmless existing sensation or lightly press a fingertip to the palm.", "Locate the exact point of sensation.", "Join awareness fully to that point.", "Notice the purity of direct sensation before the story.", "Release immediately if there is pain or distress."],
    practice: "Use a light fingertip contact instead of anything sharp.",
    mistake: "Self-injury or using pain intensity as proof of practice.",
    use: "Only as a safe modern adaptation for precise sensation.",
    avoid: "Do not pierce, cut, or harm yourself. Avoid if pain practices are triggering.",
    prompt: "What was sensation before you named it pain or pressure?",
  },
  {
    id: 71, sourceVerse: 94, family: "space", title: "Nothing Inside Me", time: 10, level: 2,
    essence: "Meditate: there is nothing inside me, no mind, intellect, bones, or organs.",
    modern: "Contemplate the inside of the person as empty of fixed identity, not as denial of the living body.",
    best: ["Self-inquiry", "Void", "Identity"],
    steps: ["Sit quietly.", "Feel the sense of me inside.", "Contemplate that no fixed mind or self can be found there.", "Let body sensations remain, but empty of identity.", "Rest without constructing a new answer."],
    practice: "Use when identity feels too solid.",
    mistake: "Using emptiness to reject the body.",
    use: "When self-inquiry is grounded and calm.",
    avoid: "Avoid during dissociation or body alienation.",
    prompt: "What remained when fixed identity could not be found inside?",
  },
  {
    id: 72, sourceVerse: 95, family: "space", title: "Names as Tempting Illusion", time: 8, level: 2,
    essence: "See named fragments as tempting illusion and understand the unity beneath them.",
    modern: "Notice how names divide reality into pieces, then rest in the unity before naming.",
    best: ["Nondual contemplation", "Perception", "Naming"],
    steps: ["Look at a simple object.", "Notice the name the mind gives it.", "Notice how naming separates it from the field.", "Let the name soften.", "Sense the object and field as one reality."],
    practice: "Use with a cup, hand, tree, or sound.",
    mistake: "Despising names; they are useful, just not ultimate.",
    use: "When perception feels overly conceptual.",
    avoid: "Avoid if you need practical naming for safety or tasks.",
    prompt: "What was present before the name appeared?",
  },
  {
    id: 73, sourceVerse: 96, family: "daily", title: "End Desire at Its Rising", time: 5, level: 2,
    essence: "When desire arises, immediately end it and be absorbed where it arose.",
    modern: "Catch the first movement of wanting. Instead of feeding it, rest attention at its source.",
    best: ["Craving", "Phone urge", "Impulse", "Desire"],
    steps: ["Notice the exact moment of wanting.", "Do not act immediately.", "Feel where the desire arose in body or mind.", "Let the desire end for one breath.", "Rest at the source of the impulse."],
    practice: "Use before unlocking the phone or reaching for a habit.",
    mistake: "Suppressing desire with aggression.",
    use: "When an impulse is mild enough to observe.",
    avoid: "Use professional support for compulsions or addictions that feel unmanageable.",
    prompt: "Where did desire arise before it became a story?",
  },
  {
    id: 74, sourceVerse: 97, family: "space", title: "Before Desire or Knowledge", time: 8, level: 3,
    essence: "When desire and knowledge have not yet arisen, ask: who am I in that condition?",
    modern: "Look for the self before wanting and before knowing appear.",
    best: ["Self-inquiry", "Deep practice", "Witnessing"],
    steps: ["Sit quietly until the mind softens.", "Notice moments before a desire or conclusion appears.", "Ask inwardly: who am I before this arises?", "Do not answer conceptually.", "Rest in the pre-arising condition."],
    practice: "Use during established seated practice.",
    mistake: "Answering with philosophy.",
    use: "When inquiry is subtle and steady.",
    avoid: "Avoid in distressed states.",
    prompt: "What is present before wanting or knowing appears?",
  },
  {
    id: 75, sourceVerse: 98, family: "space", title: "When Desire or Knowledge Has Arisen", time: 8, level: 2,
    essence: "When desire or knowledge has arisen, stop reflecting and know the self as consciousness.",
    modern: "When a desire or thought has already appeared, stop elaborating it and rest as the awareness knowing it.",
    best: ["Thoughts", "Desire", "Awareness"],
    steps: ["Notice an active desire or thought.", "Stop adding commentary.", "Recognize it as known in consciousness.", "Shift attention to the knowing itself.", "Rest there until the charge softens."],
    practice: "Use in the middle of a mental loop.",
    mistake: "Continuing to analyze while pretending to meditate.",
    use: "When thought is already active but observable.",
    avoid: "Avoid bypassing practical decisions that genuinely need action.",
    prompt: "What changed when you rested as knowing rather than reflecting?",
  },
  {
    id: 76, sourceVerse: 99, family: "space", title: "Unsupported Nature of Reality", time: 10, level: 3,
    essence: "The true nature is without cause and support, beyond ordinary perception.",
    modern: "Contemplate awareness as unsupported by any object, cause, or mental proof.",
    best: ["Formless meditation", "Advanced inquiry", "Supportless awareness"],
    steps: ["Sit after settling the mind.", "Notice each object awareness can rest on.", "Let the object go.", "Ask whether awareness itself needs support.", "Rest in the unsupported quality."],
    practice: "Use as a short formless inquiry after anchored practice.",
    mistake: "Trying to prove the unsupported with more thinking.",
    use: "When object-based attention has become quiet.",
    avoid: "Avoid if groundlessness feels frightening.",
    prompt: "Could awareness rest without leaning on an object?",
  },
  {
    id: 77, sourceVerse: 100, family: "space", title: "Same Consciousness in All Bodies", time: 10, level: 2,
    essence: "Consciousness is the essential quality in all bodies; there is no difference anywhere.",
    modern: "Sense your own consciousness and contemplate that the same knowing light shines through all beings.",
    best: ["Compassion", "Nonduality", "Relationships"],
    steps: ["Feel your own simple awareness.", "Bring another person to mind.", "Consider that awareness is present there too.", "Let the difference between bodies remain, but not the difference in consciousness.", "Rest in shared being."],
    practice: "Use after conflict to remember shared consciousness.",
    mistake: "Using sameness to erase real boundaries or differences.",
    use: "When compassion and non-separation are helpful.",
    avoid: "Do not use this to stay in unsafe relationships.",
    prompt: "How did seeing shared consciousness affect your view of another person?",
  },
  {
    id: 78, sourceVerse: 101, family: "emotion", title: "Strong Emotion, Stop the Mind", time: 5, level: 2,
    essence: "When desire, anger, greed, infatuation, intoxication, or jealousy appears, stop the mind.",
    modern: "At the peak of a strong emotion, pause the mental storyline and feel the raw energy underneath.",
    best: ["Anger", "Jealousy", "Craving", "Emotion"],
    steps: ["Notice a strong emotion rising.", "Stop the inner story for one breath.", "Feel the raw body energy.", "Do not act from the first impulse.", "Let the underlying reality of the emotion be felt."],
    practice: "Use before sending a reactive message.",
    mistake: "Suppressing the emotion instead of stopping the mental fuel.",
    use: "When the emotion is present but you can still pause safely.",
    avoid: "Step away and seek support if emotion feels unsafe or overwhelming.",
    prompt: "What was under the story of the emotion?",
  },
  {
    id: 79, sourceVerse: 102, family: "space", title: "World as Magic Picture", time: 8, level: 1,
    essence: "See the whole world as magic, picture, or illusion; joy arises.",
    modern: "Look at experience as a vivid display, like a picture or magic show, and feel the lightness this brings.",
    best: ["Lightness", "Perspective", "Daily life"],
    steps: ["Look around the room or street safely.", "See appearances as a vivid display.", "Let the sense of heaviness soften.", "Notice joy or lightness if it appears.", "Remain practical while seeing the display-like quality."],
    practice: "Use during a stressful but non-dangerous moment to loosen fixation.",
    mistake: "Denying consequences in practical life.",
    use: "When you are taking the mind's movie too seriously.",
    avoid: "Avoid if reality feels unstable or unreal in a distressing way.",
    prompt: "Did seeing life as display bring lightness or distance?",
  },
  {
    id: 80, sourceVerse: 103, family: "emotion", title: "Between Pleasure and Pain", time: 8, level: 2,
    essence: "Do not place thought on pleasure or pain; true reality lies between them.",
    modern: "Notice pleasant and unpleasant experience without leaning into either. Rest in the middle.",
    best: ["Equanimity", "Pleasure", "Pain", "Balance"],
    steps: ["Notice a pleasant or unpleasant sensation.", "Name the pull toward or away from it.", "Do not feed either movement.", "Feel the center between grasping and resisting.", "Rest there."],
    practice: "Use with mild discomfort or mild pleasure, not severe pain.",
    mistake: "Pretending pain does not matter.",
    use: "When reactive liking and disliking are visible.",
    avoid: "Do not ignore medical pain or necessary care.",
    prompt: "What was the middle like between pleasure and pain?",
  },
  {
    id: 81, sourceVerse: 104, family: "space", title: "I Am Everywhere", time: 10, level: 3,
    essence: "Leave concern for the body and believe with firm vision: I am everywhere.",
    modern: "Let identity widen beyond the body's boundary into a field-like sense of being everywhere.",
    best: ["Identity", "Expansion", "Advanced inquiry"],
    steps: ["Sit grounded in the body first.", "Feel the usual boundary of me.", "Let concern with the body soften for a moment.", "Contemplate: I am everywhere.", "Rest in the widened field without losing grounding."],
    practice: "Use briefly after body awareness, then re-ground.",
    mistake: "Using expansion to avoid the body.",
    use: "When spacious identity feels safe and clear.",
    avoid: "Avoid during dissociation or mania-like states.",
    prompt: "What happened to the boundary of me?",
  },
  {
    id: 82, sourceVerse: 105, family: "space", title: "Awareness in Objects Too", time: 10, level: 3,
    essence: "Knowledge, desire, and related powers exist not only in me but everywhere, even in objects.",
    modern: "Contemplate the same field of intelligence pervading both living experience and ordinary objects.",
    best: ["Contemplation", "Objects", "Nondual view"],
    steps: ["Choose a simple object.", "Notice awareness knowing it.", "Contemplate that it is not outside the one field.", "Let the object feel included in living presence.", "Rest in a world not divided into sacred and ordinary."],
    practice: "Use with a cup, stone, desk, or door.",
    mistake: "Forcing belief instead of exploring the felt view.",
    use: "When nondual contemplation is steady.",
    avoid: "Avoid if this blurs practical distinctions in an unhealthy way.",
    prompt: "Could an ordinary object feel included in awareness?",
  },
  {
    id: 83, sourceVerse: 106, family: "space", title: "Always Aware of the Self", time: 10, level: 3,
    essence: "All beings know subject and object; yogis are always aware of the self.",
    modern: "While objects appear, keep a subtle thread of awareness of the one who knows them.",
    best: ["Witnessing", "Self-awareness", "Daily practice"],
    steps: ["Notice an object, sound, or thought.", "Notice that it is known.", "Turn gently toward the knower.", "Keep both object and self-awareness present.", "Repeat through ordinary moments."],
    practice: "Use while walking slowly or sitting quietly.",
    mistake: "Making self-awareness tense or self-conscious.",
    use: "When you want to carry inquiry into the day.",
    avoid: "Avoid if it increases social self-consciousness or anxiety.",
    prompt: "Could you be aware of both object and knower?",
  },
  {
    id: 84, sourceVerse: 107, family: "love", title: "Same Consciousness in Self and Others", time: 8, level: 2,
    essence: "Leave body-concern and believe the same consciousness is in oneself and other bodies.",
    modern: "Sense that the awareness in you and the awareness in others is not fundamentally separate.",
    best: ["Compassion", "Connection", "Conflict repair"],
    steps: ["Feel your own presence.", "Bring another person to mind.", "Sense their body and life as real.", "Contemplate the same consciousness in both.", "Let the heart soften without losing boundaries."],
    practice: "Use before a difficult conversation.",
    mistake: "Confusing shared consciousness with agreement or permission.",
    use: "When you need connection without collapse.",
    avoid: "Do not use this to override safety or boundaries.",
    prompt: "How did shared consciousness affect the feeling of separation?",
  },
  {
    id: 85, sourceVerse: 108, family: "space", title: "Stopping All Thoughts", time: 8, level: 3,
    essence: "By stopping all thoughts, the unsupported mind becomes the supreme self.",
    modern: "Let thoughts stop for a moment not by violence, but by refusing to feed the next movement.",
    best: ["Thoughts", "Advanced practice", "Stillness"],
    steps: ["Sit after settling with breath or sound.", "Notice the next thought impulse.", "Do not follow it.", "Rest in the unsupported pause.", "Repeat without aggression."],
    practice: "Use as a brief advanced practice after easier anchors.",
    mistake: "Fighting thought, which creates more thought.",
    use: "When the mind is already relatively quiet.",
    avoid: "Avoid if thought-stopping becomes repression or distress.",
    prompt: "What supported awareness when thought paused?",
  },
  {
    id: 86, sourceVerse: 109, family: "space", title: "Qualities of Shiva", time: 10, level: 3,
    essence: "Firmly believe: the qualities of God are also present here.",
    modern: "Contemplate awareness as already luminous, capable, and pervading, rather than small and deficient.",
    best: ["Devotion", "Confidence", "Identity"],
    steps: ["Sit with a respectful mood.", "Name the qualities the source gives to Shiva: knowing, power, presence.", "Contemplate those qualities as not separate from awareness.", "Feel how the body-mind responds.", "Rest in dignity, not ego inflation."],
    practice: "Use when spiritual confidence feels weak.",
    mistake: "Turning sacred identity into personal grandiosity.",
    use: "When devotion and humility are present.",
    avoid: "Avoid if expansive beliefs become destabilizing or inflated.",
    prompt: "Could sacred dignity be felt without ego inflation?",
  },
  {
    id: 87, sourceVerse: 110, family: "space", title: "Forms Arising From Me", time: 10, level: 3,
    essence: "As waves arise from water and light from the sun, forms arise from the divine self.",
    modern: "Contemplate all forms as waves of one underlying reality, not separate things.",
    best: ["Nondual contemplation", "Waves", "Unity"],
    steps: ["Bring to mind waves arising from water.", "Look at thoughts, sensations, and objects as waves.", "Ask what the waves are made of.", "Let forms return to the one field.", "Rest as the field, not a single wave."],
    practice: "Use near water or with the image of waves.",
    mistake: "Trying to eliminate forms instead of seeing their source.",
    use: "When unity contemplation feels natural.",
    avoid: "Avoid if formless contemplation feels ungrounding.",
    prompt: "What was the water beneath today's waves?",
  },
  {
    id: 88, sourceVerse: 111, family: "body", title: "After Spinning Stops", time: 3, level: 3,
    essence: "Turn the body round until falling; at the end of motion, the supreme state is born.",
    modern: "Use only a safe adaptation: a few gentle turns, then feel the stillness after motion ends.",
    best: ["Movement", "Stillness", "Advanced adaptation"],
    steps: ["Practice only in a clear safe space.", "Turn slowly once or twice, or rotate in a chair gently.", "Stop before dizziness becomes strong.", "Stand or sit safely and feel the after-motion.", "Attend to the stillness beneath movement."],
    practice: "Use the smallest safe amount of motion that reveals after-stillness.",
    mistake: "Spinning until falling or risking injury.",
    use: "Only if balance is healthy and the setting is safe.",
    avoid: "Avoid with vertigo, pregnancy, balance issues, injury risk, or unsafe surroundings.",
    prompt: "What was still after motion stopped?",
  },
  {
    id: 89, sourceVerse: 112, family: "emotion", title: "When Energy or Knowledge Fades", time: 6, level: 2,
    essence: "When energy or knowledge is lacking, the mind dissolves into energy; when energy subsides, God appears.",
    modern: "In moments of low energy or not-knowing, stop struggling and attend to the dissolving of the mind.",
    best: ["Tiredness", "Not knowing", "Surrender"],
    steps: ["Notice low energy, blankness, or not-knowing.", "Stop trying to manufacture clarity.", "Feel the mind's inability to continue.", "Rest in the quiet that follows.", "Let energy return naturally."],
    practice: "Use when tired but not when you need sleep or medical care.",
    mistake: "Forcing insight when the doorway is surrender.",
    use: "When low energy is safe to observe.",
    avoid: "Do not use this to ignore exhaustion, illness, depression, or needed rest.",
    prompt: "What appeared when you stopped forcing energy or answers?",
  },
  {
    id: 90, sourceVerse: 113, family: "space", title: "Fixed Unblinking Eyes", time: 5, level: 2,
    essence: "If the eyes are fixed without blinking, unification with the supreme occurs.",
    modern: "Use a soft, steady gaze as a doorway into unified attention, blinking when needed.",
    best: ["Gazing", "Concentration", "Stillness"],
    steps: ["Choose a safe, neutral point.", "Rest the eyes there softly.", "Let blinking reduce naturally without force.", "Feel attention unify around seeing.", "Close the eyes if strain appears."],
    practice: "Use a small mark on the wall for a few minutes.",
    mistake: "Competing with the blink reflex.",
    use: "When gazing feels calming and precise.",
    avoid: "Avoid with eye strain, dry eyes, or migraine.",
    prompt: "Did steady seeing unify attention?",
  },
  {
    id: 91, sourceVerse: 114, family: "sound", title: "Inner Sound Without Letters", time: 5, level: 3,
    essence: "Close the ears and attend to sound without vowel or consonant.",
    modern: "Use a safe adaptation: cover the ears gently and listen for the inner sound that is not a spoken syllable.",
    best: ["Inner sound", "Advanced practice", "Silence"],
    steps: ["Sit comfortably.", "Gently cover the ears without pressure.", "Listen inwardly for hum, vibration, or silence.", "Do not label it as a word or letter.", "Release the ears slowly."],
    practice: "Use briefly after external listening practice.",
    mistake: "Adding advanced locks or pressure without guidance.",
    use: "When inner sound is accessible and calming.",
    avoid: "Avoid with ear pain, tinnitus distress, pelvic-floor issues, or without guidance for the full traditional form.",
    prompt: "Was the inner sound a tone, hum, vibration, or silence?",
  },
  {
    id: 92, sourceVerse: 115, family: "space", title: "The Deep Hollow", time: 6, level: 2,
    essence: "Look without blinking at a deep well or hollow space until the mind dissolves.",
    modern: "Gaze safely into a sense of depth: a well-like hollow, stairwell, bowl, or imagined depth.",
    best: ["Depth", "Void", "Gazing"],
    steps: ["Choose a safe depth view; do not lean over edges.", "Look softly into the hollow space.", "Let attention fall into depth.", "Blink as needed.", "Rest as thought quiets."],
    practice: "Use a bowl, dark cup, or safe architectural depth.",
    mistake: "Risking physical safety to find dramatic depth.",
    use: "When depth naturally draws the gaze inward.",
    avoid: "Avoid heights, wells, ledges, or unsafe places.",
    prompt: "What did depth do to thought?",
  },
  {
    id: 93, sourceVerse: 116, family: "daily", title: "Wherever Mind Goes Is Shiva", time: 8, level: 2,
    essence: "Wherever the mind goes, externally or internally, there is the form of Shiva.",
    modern: "Do not reject where attention moves. Recognize each inner or outer object as appearing within the same sacred field.",
    best: ["Distraction", "Daily life", "Nondual practice"],
    steps: ["Let the mind move naturally.", "Notice where it goes.", "Instead of pulling it back harshly, recognize the same awareness there.", "Rest with that object as included.", "Repeat with the next movement."],
    practice: "Use when distraction would otherwise become frustration.",
    mistake: "Using this as an excuse for unconscious wandering.",
    use: "When attention is mobile but awareness can remain awake.",
    avoid: "Avoid if you need a tighter anchor for safety or emotional regulation.",
    prompt: "Could even distraction be included in awareness?",
  },
  {
    id: 94, sourceVerse: 117, family: "daily", title: "Heightened Sense Awareness", time: 5, level: 1,
    essence: "Whenever awareness increases through any sense organ, remain in that awareness.",
    modern: "When a sound, sight, smell, taste, or touch becomes vivid, stay with the vivid awareness itself.",
    best: ["Senses", "Daily life", "Beginner", "Presence"],
    steps: ["Notice any sense becoming vivid.", "Pause and stay with that vividness.", "Do not immediately name or evaluate it.", "Feel awareness brighten through the sense.", "Rest until the intensity fades."],
    practice: "Use with birdsong, fragrance, a color, or warm water on the hands.",
    mistake: "Chasing more stimulation.",
    use: "Whenever ordinary sense experience becomes alive.",
    avoid: "Avoid overwhelming stimuli.",
    prompt: "Which sense opened awareness today?",
  },
  {
    id: 95, sourceVerse: 118, family: "daily", title: "Transitions of Shock and Intensity", time: 3, level: 2,
    essence: "The beginning and end of sneeze, danger, sorrow, weeping, flight, curiosity, or hunger reveal the state of God.",
    modern: "At sharp transitions, before and after intense bodily or emotional events, notice the sudden gap in ordinary mind.",
    best: ["Transition", "Fear", "Sorrow", "Curiosity"],
    steps: ["Use only naturally occurring transitions.", "Notice the instant before or after a sneeze, strong feeling, hunger, or curiosity.", "Pause safely.", "Feel the gap in the usual mind.", "Let awareness settle before acting."],
    practice: "Use the moment after a sneeze or wave of tears as a short doorway.",
    mistake: "Creating danger or distress to practice.",
    use: "When intensity arises naturally and you are safe.",
    avoid: "Never manufacture danger; seek support for severe distress.",
    prompt: "Which transition revealed a brief gap in ordinary mind?",
  },
  {
    id: 96, sourceVerse: 119, family: "space", title: "Memory Without Body-Concern", time: 8, level: 2,
    essence: "Leaving concern for the body, remember a place, object, or incident; the mind loses support.",
    modern: "Recall a vivid place or object while letting body-concern relax, then notice the unsupported quality of memory.",
    best: ["Memory", "Visualization", "Letting go"],
    steps: ["Sit safely and comfortably.", "Recall a place, object, or incident vividly.", "Let concern with the present body soften.", "Notice memory as an appearance in awareness.", "Rest when the image loses support."],
    practice: "Use a peaceful memory, such as a landscape or room.",
    mistake: "Choosing a memory that pulls you into rumination.",
    use: "When memory can be held lightly.",
    avoid: "Avoid traumatic or obsessive memories.",
    prompt: "What happened when memory was seen as an appearance?",
  },
  {
    id: 97, sourceVerse: 120, family: "space", title: "Withdraw Sight Into Void", time: 6, level: 2,
    essence: "After looking at an object, slowly withdraw sight, knowledge, and thought of it into the void.",
    modern: "Look at an object, then close or soften the gaze and let the object, name, and thought dissolve.",
    best: ["Visual meditation", "Void", "Letting go"],
    steps: ["Choose a simple object.", "Look at it clearly.", "Withdraw the gaze slowly or close the eyes.", "Let the image, name, and thought of it fade.", "Rest in the void after the object."],
    practice: "Use with one object on your desk before starting work.",
    mistake: "Keeping the object mentally alive through commentary.",
    use: "When visual letting-go is accessible.",
    avoid: "Avoid if closing eyes causes discomfort.",
    prompt: "What remained after the object and its name faded?",
  },
  {
    id: 98, sourceVerse: 121, family: "love", title: "Devotion and Detachment", time: 10, level: 2,
    essence: "From abundant devotion and detachment, divine energy is born; continuously be her.",
    modern: "Let love and non-clinging arise together. Rest as the energy born from devotion without demand.",
    best: ["Devotion", "Love", "Surrender", "Detachment"],
    steps: ["Bring to mind what you deeply revere or love.", "Feel devotion in the body.", "Release the need to possess or control it.", "Let devotion and detachment meet.", "Rest as the energy that remains."],
    practice: "Use after prayer, gratitude, or a moment of awe.",
    mistake: "Confusing devotion with clinging.",
    use: "When love is present but you want it purified of grasping.",
    avoid: "Avoid using detachment to deny care or responsibility.",
    prompt: "How did devotion change when clinging softened?",
  },
  {
    id: 99, sourceVerse: 122, family: "space", title: "Object Empty Inside", time: 8, level: 1,
    essence: "Understand an object as empty inside, and emptiness as a feature of all objects.",
    modern: "Look at an object and contemplate that its apparent solidity is not ultimate; it is empty, open, and dependent.",
    best: ["Objects", "Emptiness", "Beginner"],
    steps: ["Choose a simple object.", "Look at its surface and shape.", "Contemplate the openness inside and around it.", "Let the sense of solidity soften.", "Rest in calm perception."],
    practice: "Use a cup, stone, or piece of fruit.",
    mistake: "Denying practical solidity; this is contemplative, not practical physics.",
    use: "When everyday objects feel like useful meditation supports.",
    avoid: "Avoid if emptiness contemplation feels bleak.",
    prompt: "How did the object change when seen as empty inside?",
  },
  {
    id: 100, sourceVerse: 123, family: "space", title: "Beyond Pure and Impure", time: 8, level: 2,
    essence: "What is called pure or impure is not ultimately pure or impure; rise beyond dualizing thought.",
    modern: "Notice the mind labeling experience as pure or impure, acceptable or unacceptable, and rest before that split.",
    best: ["Judgment", "Nonduality", "Acceptance"],
    steps: ["Notice a judgment of pure, impure, good, or bad.", "Feel the contraction around the label.", "Do not argue with the label.", "Rest in awareness before the duality.", "Act ethically from clarity, not aversion."],
    practice: "Use when self-judgment becomes harsh.",
    mistake: "Using nonduality to excuse harmful action.",
    use: "When judgment is mental and reactive.",
    avoid: "Do not bypass ethical discernment or safety.",
    prompt: "What was present before the pure/impure label?",
  },
  {
    id: 101, sourceVerse: 124, family: "space", title: "God Existing Everywhere", time: 10, level: 2,
    essence: "God exists everywhere; there is nothing else other than God.",
    modern: "Let everything you meet be included in one divine or sacred presence.",
    best: ["Devotion", "Awe", "Unity", "Daily life"],
    steps: ["Sit or walk slowly.", "Let attention include each object and person.", "Contemplate: this too is not outside the sacred.", "Notice resistance or warmth.", "Rest in the all-inclusive field."],
    practice: "Use secular wording like 'one sacred field' if God language does not fit.",
    mistake: "Forcing belief instead of exploring perception.",
    use: "When devotional or sacred perception supports you.",
    avoid: "Do not use this to deny harm or avoid wise action.",
    prompt: "What was hardest to include in the sacred field?",
  },
  {
    id: 102, sourceVerse: 125, family: "emotion", title: "Equal Toward Friend and Enemy", time: 10, level: 3,
    essence: "Knowing the divine fills everything, become the same toward enemy and friend, honor and dishonor.",
    modern: "Practice equanimity by seeing praise and blame, friend and enemy, within the same field of awareness.",
    best: ["Equanimity", "Conflict", "Praise and blame"],
    steps: ["Bring to mind someone easy and someone difficult.", "Notice different reactions.", "Include both in the same awareness.", "Bring to mind praise and criticism.", "Let awareness remain even toward both."],
    practice: "Use after criticism or praise to regain balance.",
    mistake: "Pretending boundaries are unnecessary.",
    use: "When emotional reactivity around people is visible.",
    avoid: "Do not use equanimity to remain in unsafe situations.",
    prompt: "Where did equality feel possible, and where did it not?",
  },
  {
    id: 103, sourceVerse: 126, family: "emotion", title: "Center Between Aversion and Attachment", time: 8, level: 2,
    essence: "Have neither aversion nor attachment; remain in the center between the two.",
    modern: "Notice pushing away and pulling toward. Rest in the center before either movement takes over.",
    best: ["Craving", "Aversion", "Balance", "Relationships"],
    steps: ["Choose a mild object of like or dislike.", "Feel the pull toward or push away.", "Do not act immediately.", "Sense the center between the two.", "Rest there until the reaction loosens."],
    practice: "Use before reacting to a person, place, or notification.",
    mistake: "Mistaking the center for passivity.",
    use: "When attachment or aversion is mild enough to observe.",
    avoid: "Strong danger requires action, not neutrality practice.",
    prompt: "What did the center between pull and push feel like?",
  },
  {
    id: 104, sourceVerse: 127, family: "space", title: "Beyond Knowing and Grasping", time: 10, level: 3,
    essence: "Contemplate what is beyond knowledge, grasping, and not-being as God.",
    modern: "Let attention open toward what cannot be captured by concept, possession, or negation.",
    best: ["Mystery", "Formless meditation", "Advanced inquiry"],
    steps: ["Sit after settling.", "Notice what the mind can know.", "Notice the urge to grasp or define.", "Let both knowing and grasping fall away.", "Rest with mystery as the doorway."],
    practice: "Use at the end of study or reading, when concepts are ready to dissolve.",
    mistake: "Making mystery into another concept.",
    use: "When the mind is mature enough to release answers.",
    avoid: "Avoid if not-knowing creates panic.",
    prompt: "Could you rest without grasping an answer?",
  },
  {
    id: 105, sourceVerse: 128, family: "space", title: "External Space as Silent and Supportless", time: 10, level: 2,
    essence: "Fix the mind on external space: eternal, supportless, void, all-pervasive, and silent.",
    modern: "Let attention rest in open external space and absorb its silence and supportlessness.",
    best: ["Space", "Silence", "Sky", "Stillness"],
    steps: ["Look at open space in a room or outside.", "Sense its silence.", "Notice that it does not need support.", "Let the mind take on those qualities.", "Rest in open, silent space."],
    practice: "Use a blank wall, open room, or sky view.",
    mistake: "Looking for an object in space instead of resting in space itself.",
    use: "When spacious silence is accessible.",
    avoid: "Avoid unsafe open places or eye strain.",
    prompt: "Did external space feel silent or alive?",
  },
  {
    id: 106, sourceVerse: 129, family: "daily", title: "Leave Each Thought Where It Goes", time: 6, level: 2,
    essence: "Wherever the mind goes, leave that thought at that very moment.",
    modern: "When thought moves, release it immediately instead of letting the mind settle into it.",
    best: ["Overthinking", "Thoughts", "Letting go"],
    steps: ["Sit and watch the mind.", "When a thought appears, recognize it.", "Leave it without completing the story.", "Return to open awareness.", "Repeat with the next thought."],
    practice: "Use during planning loops or worry loops.",
    mistake: "Finishing the thought first and then trying to release it.",
    use: "When thoughts are light enough to be dropped.",
    avoid: "Write down genuinely important tasks before practicing if needed.",
    prompt: "Which thoughts were hardest to leave immediately?",
  },
  {
    id: 107, sourceVerse: 130, family: "sound", title: "Reciting Bhairava", time: 8, level: 2,
    essence: "Since God pervades everything and every sound, continuous recitation of Bhairava leads to God.",
    modern: "Use the name Bhairava as mantra, letting the sound point back to the all-pervading reality it names.",
    best: ["Mantra", "Devotion", "Sound"],
    steps: ["Sit with respect for the name.", "Recite Bhairava aloud or silently.", "Feel the vibration and meaning together.", "Let the recitation become continuous and gentle.", "Rest in the presence the name evokes."],
    practice: "Use a mala or timer if mantra rhythm helps.",
    mistake: "Repeating mechanically without presence.",
    use: "When devotional mantra fits your path.",
    avoid: "Choose another respectful sacred name if this one does not belong in your practice.",
    prompt: "Did the name become sound, meaning, presence, or all three?",
  },
  {
    id: 108, sourceVerse: 131, family: "daily", title: "When I and Mine Arise", time: 8, level: 2,
    essence: "When the assertion I am or this is mine arises, let the mind go to the supportless.",
    modern: "Use moments of I, me, or mine as a trigger to release into awareness without support.",
    best: ["Ego", "Identity", "Daily inquiry"],
    steps: ["Notice the phrase or feeling: I, me, or mine.", "Pause before defending it.", "Feel the contraction of ownership.", "Let the mind move into supportless awareness.", "Act afterward from clarity."],
    practice: "Use when possessiveness or self-importance appears.",
    mistake: "Condemning the ego instead of using it as a doorway.",
    use: "When identity contraction is noticeable.",
    avoid: "Do not use this to erase healthy boundaries or responsibilities.",
    prompt: "What happened when mine became a doorway?",
  },
  {
    id: 109, sourceVerse: 132, family: "space", title: "Eternal and Supportless", time: 10, level: 2,
    essence: "Meditate on eternal, omnipresent, supportless, all-pervasive reality.",
    modern: "Repeat these contemplative qualities and let attention conform to them: eternal, everywhere, supportless, all-pervading.",
    best: ["Contemplation", "Sacred qualities", "Stability"],
    steps: ["Sit quietly.", "Bring one quality to mind: eternal.", "Then omnipresent, supportless, all-pervasive.", "Let each word shape attention.", "Rest in the felt sense beyond the words."],
    practice: "Use one quality per day for a slower practice.",
    mistake: "Reciting qualities without feeling their effect.",
    use: "When contemplative language focuses the mind.",
    avoid: "Avoid if grand abstractions disconnect you from reality.",
    prompt: "Which quality most changed your state of mind?",
  },
  {
    id: 110, sourceVerse: 133, family: "space", title: "The Universe as Illusion", time: 8, level: 2,
    essence: "The universe is like illusion or magic; firmly seeing this, one abides in peace.",
    modern: "Contemplate the world as appearance: vivid, functional, but not as solid as the mind insists.",
    best: ["Perspective", "Peace", "Detachment"],
    steps: ["Look at the field of experience.", "Notice how vivid it is.", "Contemplate that vivid appearance is not ultimate solidity.", "Let fixation soften.", "Rest in peace, while remaining practical."],
    practice: "Use when caught in a mental drama.",
    mistake: "Using illusion language to deny care, ethics, or consequences.",
    use: "When perspective is needed.",
    avoid: "Avoid if unreality feels distressing or destabilizing.",
    prompt: "Did seeing appearance as less solid bring peace?",
  },
  {
    id: 111, sourceVerse: 134, family: "space", title: "The Unchangeable Self", time: 10, level: 3,
    essence: "The self is unchangeable; external existence and objects depend on knowledge, so the world is void.",
    modern: "Notice changing experiences and inquire into the unchanging awareness in which they are known.",
    best: ["Self-inquiry", "Change", "Void"],
    steps: ["Notice a changing sensation.", "Notice a changing thought.", "Notice a changing perception.", "Ask what knows all these changes.", "Rest as the changeless knowing, not the changing content."],
    practice: "Use after a day of emotional change.",
    mistake: "Calling the self unchanging as a belief instead of an inquiry.",
    use: "When change feels overwhelming and inquiry feels steady.",
    avoid: "Avoid if inquiry creates numb detachment.",
    prompt: "What knew the changing experiences?",
  },
  {
    id: 112, sourceVerse: 135, family: "space", title: "No Bondage, No Liberation", time: 10, level: 3,
    essence: "Neither bondage nor liberation belongs to the self; these concepts are images of mind.",
    modern: "Let even the spiritual ideas of being trapped or becoming free dissolve as thoughts appearing in awareness.",
    best: ["Advanced inquiry", "Spiritual pressure", "Freedom"],
    steps: ["Sit quietly.", "Bring to mind the feeling of being bound.", "See it as an image or thought in the mind.", "Bring to mind the idea of liberation.", "Let both dissolve into simple awareness."],
    practice: "Use when spiritual striving becomes heavy.",
    mistake: "Using no bondage as an excuse to avoid practice or responsibility.",
    use: "When you are ready to release even spiritual concepts.",
    avoid: "Avoid if it leads to apathy or bypassing real-life needs.",
    prompt: "What remained when both bondage and liberation were seen as thoughts?",
  },
];

const defaultAvoid = "Practice gently. Stop if the method creates strain, fear, dissociation, or physical discomfort.";

function relatedFor(item) {
  return rawTechniques
    .filter((candidate) => candidate.family === item.family && candidate.id !== item.id)
    .slice(0, 3)
    .map((candidate) => candidate.id);
}

export const techniques = rawTechniques.map((item) => ({
  ...item,
  source: `Vigyan Bhairava Tantra, verse ${item.sourceVerse}`,
  related: item.related ?? relatedFor(item),
  avoid: item.avoid ?? defaultAvoid,
}));

// ============ HELPERS ============
export const techniquesById = Object.fromEntries(techniques.map((t) => [t.id, t]));

export function techniquesByFamily(family) {
  return techniques.filter((t) => t.family === family);
}

export function filterTechniques({ family, time, level, query } = {}) {
  return techniques.filter((t) => {
    if (family && family !== "all" && t.family !== family) return false;
    if (time && time !== "all") {
      const max = Number(time);
      if (!Number.isNaN(max) && t.time > max) return false;
    }
    if (level && level !== "all" && t.level !== Number(level)) return false;
    if (query) {
      const q = query.toLowerCase();
      const hay = [
        t.title,
        t.modern,
        t.essence,
        t.source,
        t.best.join(" "),
      ].join(" ").toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}

export const moodToTechnique = {
  restless: 1,
  phone: 73,
  angry: 78,
  fearful: 95,
  tired: 89,
  sleep: 52,
  lonely: 48,
  focus: 10,
  curious: 74,
  silent: 16,
};
